/**
 * FR-130…135 · Nhật ký hoạt động — bám bố cục `audit-log.html`:
 *
 *   page-head (chọn khoảng thời gian · Xuất nhật ký)
 *   section.grid.g-4                4 thẻ `.stat`
 *   div.grid.g-side.mt-20
 *     section.card                  card-head[filter-pills + search] ·
 *                                   table-wrap > table.tbl#audit-table · card-foot
 *     aside.stack                   card "Cảnh báo cần xử lý" (ol.timeline) ·
 *                                   card "Phân bố sự kiện" (.bar)
 *
 * FR-133: phạm vi do máy chủ quyết định; câu dẫn đổi theo `scopeAll`.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, apiWithMeta, API_PREFIX } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import { avatarTone, hienIp, initialsOf, useFormat } from '../../lib/format';
import { SearchBox } from '../../components/SearchBox';
import { Pagination } from '../../components/Pagination';
import { usePhanTrangUrl } from '../../lib/phan-trang';
import { actionKhopNhan } from '../../lib/action-search';
import { AsyncSection, EmptyState, SkeletonCard } from '../../components/States';
import { AuditRowDetail } from './AuditRowDetail';
import type { AuditCategory, AuditCategoryDef, AuditLogItem, AuditPayload } from './types';

/** OQ-262 — mặc định 25 (docs/04), người dùng đổi được qua ô chọn. */
const COLUMNS = 6;

const RANGES = ['24h', '7d', '30d', 'custom'] as const;
type Range = (typeof RANGES)[number];

const PILLS: ('all' | AuditCategory)[] = ['all', 'dns', 'domain', 'user', 'security', 'system'];

function rangeStart(range: Range): string | null {
  const hours = range === '24h' ? 24 : range === '7d' ? 24 * 7 : range === '30d' ? 24 * 30 : 0;
  if (hours === 0) return null;
  return new Date(Date.now() - hours * 3600 * 1000).toISOString();
}

export interface AuditLogPageProps {
  /** FR-045 — lọc sẵn theo một tên miền khi nhúng vào `domain-detail.html`. */
  domainId?: string;
  /** Bỏ `page-head` và bốn thẻ số liệu khi nằm trong tabpanel. */
  embedded?: boolean;
}

export function AuditLogPage({ domainId, embedded = false }: AuditLogPageProps = {}) {
  const { formatDateTime, formatNumber } = useFormat();
  const t = useT();
  const { can } = useAuth();

  const [range, setRange] = useState<Range>('7d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [pill, setPill] = useState<'all' | AuditCategory>('all');
  const [search, setSearch] = useState('');
  /** OQ-252 — lọc theo kết quả: thành công / thất bại. */
  const [result, setResult] = useState<'' | 'success' | 'failure'>('');
  const { page, perPage, datTrang, datSoDong, datLoc } = usePhanTrangUrl();
  const [openRow, setOpenRow] = useState<string | null>(null);

  const categoriesQuery = useQuery({
    queryKey: ['audit-logs', 'categories'],
    queryFn: () => api.get<AuditCategoryDef[]>('/audit-logs/categories'),
    staleTime: 30 * 60 * 1000,
  });

  const filters = useMemo(() => {
    const params = new URLSearchParams();
    if (domainId) params.set('domainId', domainId);
    if (range === 'custom') {
      if (customFrom) params.set('from', new Date(`${customFrom}T00:00:00`).toISOString());
      if (customTo) params.set('to', new Date(`${customTo}T23:59:59`).toISOString());
    } else {
      const from = rangeStart(range);
      if (from) params.set('from', from);
    }
    if (search.trim()) {
      params.set('q', search.trim());
      /*
       * OQ-252 — người dùng gõ NHÃN hành động ("Thêm bản ghi DNS") chứ không gõ
       * mã. Bản dịch nằm ở phía web nên phải tra ngược tại đây rồi gửi kèm; API
       * nối danh sách này vào mệnh đề tìm bằng OR.
       */
      const khop = actionKhopNhan(search, t);
      if (khop.length > 0) params.set('qActions', khop.join(','));
    }
    if (result) params.set('result', result);
    if (pill !== 'all') {
      const actions = categoriesQuery.data?.find((def) => def.category === pill)?.actions ?? [];
      if (actions.length > 0) params.set('action', actions.join(','));
    }
    return params;
  }, [domainId, range, customFrom, customTo, search, result, pill, categoriesQuery.data, t]);

  const query = useMemo(() => {
    const params = new URLSearchParams(filters);
    params.set('page', String(page));
    params.set('perPage', String(perPage));
    return params;
    /*
     * `perPage` PHẢI có trong deps — OQ-262.
     *
     * Thiếu nó thì `useMemo` giữ nguyên chuỗi cũ khi người dùng đổi số dòng:
     * `queryKey` không đổi, React Query không tải lại, và danh sách vẫn 25 dòng
     * dù ô chọn đã hiện 100. Ô chọn trông như hỏng, mà thật ra nó gọi đúng —
     * chỉ là không ai nghe.
     */
  }, [filters, page, perPage]);

  const logsQuery = useQuery({
    queryKey: ['audit-logs', query.toString()],
    queryFn: () => apiWithMeta<AuditPayload>(`/audit-logs?${query.toString()}`),
  });

  const payload = logsQuery.data?.data;
  const meta = logsQuery.data?.meta;
  const stats = payload?.stats;
  const items = payload?.items ?? [];

  const distribution: { key: AuditCategory; value: number; tone: string }[] = [
    { key: 'dns', value: stats?.dns ?? 0, tone: '' },
    { key: 'domain', value: stats?.domain ?? 0, tone: '' },
    { key: 'user', value: stats?.user ?? 0, tone: '' },
    { key: 'security', value: stats?.security ?? 0, tone: 'warn' },
    { key: 'system', value: stats?.system ?? 0, tone: 'ok' },
  ];
  const peak = Math.max(1, ...distribution.map((row) => row.value));

  const exportHref = (format: 'csv' | 'json') => {
    const params = new URLSearchParams(filters);
    params.set('format', format);
    return `${API_PREFIX}/audit-logs/export?${params.toString()}`;
  };

  return (
    <div data-od-id={embedded ? 'panel-log-audit' : 'page-audit'}>
      {embedded ? null : (
        <div className="page-head">
          <div className="grow">
            <h1 data-od-id="audit-title">{t('audit.title')}</h1>
            <p className="lede">
              {payload?.scopeAll === false ? t('audit.ledeUser') : t('audit.ledeAdmin')}
            </p>
          </div>
          <div className="row">
            <select
              style={{ width: 160 }}
              data-od-id="audit-range"
              aria-label={t('audit.range')}
              value={range}
              onChange={(event) => {
                setRange(event.target.value as Range);
                datLoc({});
              }}
            >
              {RANGES.map((name) => (
                <option key={name} value={name}>
                  {t(`audit.range.${name}`)}
                </option>
              ))}
            </select>

            {range === 'custom' ? (
              <>
                <input
                  type="date"
                  aria-label={t('audit.from')}
                  value={customFrom}
                  onChange={(event) => {
                    setCustomFrom(event.target.value);
                    datLoc({});
                  }}
                />
                <input
                  type="date"
                  aria-label={t('audit.to')}
                  value={customTo}
                  onChange={(event) => {
                    setCustomTo(event.target.value);
                    datLoc({});
                  }}
                />
              </>
            ) : null}

            {can('adm.audit') ? (
              <>
                <a className="btn" href={exportHref('csv')} data-od-id="audit-export">
                  {t('audit.export')}
                </a>
                <a className="btn btn-ghost" href={exportHref('json')}>
                  JSON
                </a>
              </>
            ) : null}
          </div>
        </div>
      )}

      {embedded ? null : (
        <section className="grid g-4" data-od-id="audit-stats">
          <div className="card stat">
            <div className="label">{t('audit.stat.total')}</div>
            <div className="value num">{formatNumber(stats?.total ?? 0)}</div>
            <div className="delta">{t(`audit.range.${range}`)}</div>
          </div>
          <div className="card stat">
            <div className="label">{t('audit.stat.dns')}</div>
            <div className="value num">{formatNumber(stats?.dns ?? 0)}</div>
            <div className="delta">{t('audit.stat.dnsSub')}</div>
          </div>
          <div className="card stat">
            <div className="label">{t('audit.stat.security')}</div>
            <div className="value num">{formatNumber(stats?.security ?? 0)}</div>
            <div className={stats?.failures ? 'delta warn' : 'delta'}>
              {stats?.failures ? (
                <>
                  <b>{stats.failures}</b>
                  <span>{t('audit.stat.failures')}</span>
                </>
              ) : (
                t('audit.stat.noFailure')
              )}
            </div>
          </div>
          <div className="card stat">
            <div className="label">{t('audit.stat.permissions')}</div>
            <div className="value num">{formatNumber(stats?.user ?? 0)}</div>
            <div className="delta">{t('audit.stat.permissionsSub')}</div>
          </div>
        </section>
      )}

      <div className={embedded ? '' : 'grid g-side mt-20'}>
        <section className="card" data-od-id="audit-table-card">
          <div className="card-head">
            <div className="toolbar grow">
              <div className="filter-pills" data-od-id="audit-filter">
                {PILLS.map((name) => (
                  <button
                    key={name}
                    type="button"
                    aria-pressed={pill === name}
                    onClick={() => {
                      setPill(name);
                      datLoc({});
                    }}
                  >
                    {t(`audit.filter.${name}`)}
                  </button>
                ))}
              </div>
              <SearchBox
                value={search}
                onChange={(next) => {
                  setSearch(next);
                  datLoc({});
                }}
                placeholder={t('audit.searchPlaceholder')}
                odId="audit-search"
              />
              {/* OQ-252 — lọc theo kết quả, cộng dồn với từ khoá và bộ lọc nhóm. */}
              <select
                className="lvl loc-gon"
                aria-label={t('audit.filterResultLabel')}
                data-od-id="audit-result-filter"
                value={result}
                onChange={(event) => {
                  setResult(event.target.value as '' | 'success' | 'failure');
                  datLoc({});
                }}
              >
                <option value="">{t('audit.filterResultAll')}</option>
                <option value="success">{t('result.success')}</option>
                <option value="failure">{t('result.failure')}</option>
              </select>
            </div>
            <span className="small muted num">
              {items.length} / {formatNumber(meta?.total ?? 0)}
            </span>
          </div>

          <AsyncSection
            loading={logsQuery.isLoading}
            error={logsQuery.error}
            data={items.length > 0 ? items : undefined}
            onRetry={() => void logsQuery.refetch()}
            skeleton={
              <div className="card-body">
                <SkeletonCard rows={6} />
              </div>
            }
            empty={
              <EmptyState
                icon="audit"
                title={t('audit.empty.title')}
                body={t('audit.empty.body')}
              />
            }
          >
            {(rows) => (
              <div className="table-wrap">
                <table className="tbl" id="audit-table" data-od-id="audit-table">
                  <thead>
                    <tr>
                      <th scope="col">{t('audit.col.at')}</th>
                      <th scope="col">{t('audit.col.actor')}</th>
                      <th scope="col">{t('audit.col.action')}</th>
                      <th scope="col">{t('audit.col.target')}</th>
                      <th scope="col">{t('audit.col.ip')}</th>
                      <th scope="col" className="right">
                        {t('audit.col.result')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row: AuditLogItem) => (
                      <FragmentRow
                        key={row.id}
                        row={row}
                        open={openRow === row.id}
                        onToggle={() => setOpenRow((prev) => (prev === row.id ? null : row.id))}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </AsyncSection>

          <div className="card-foot">
            <span className="small muted">{t('audit.retention')}</span>
            <span style={{ flex: 1 }} />
            <Pagination
              page={page}
              perPage={perPage}
              total={meta?.total ?? 0}
              odId="audit-pager"
              onPage={datTrang}
              onPerPage={datSoDong}
            />
          </div>
        </section>

        <aside className="stack" hidden={embedded}>
          <div className="card" data-od-id="audit-attention">
            <div className="card-head">
              <div className="grow">
                <h2>{t('audit.attention.title')}</h2>
                <div className="sub">{t('audit.attention.sub')}</div>
              </div>
            </div>
            <div className="card-body">
              {payload && payload.attention.length > 0 ? (
                <ol className="timeline">
                  {payload.attention.map((item, index) => (
                    <li key={`${item.kind}-${index}`} className={`tl-item ${item.level}`}>
                      <div className="when">{formatDateTime(item.at)}</div>
                      <b>{item.title}</b>
                      <div className="small muted">{item.body}</div>
                    </li>
                  ))}
                </ol>
              ) : (
                <EmptyState icon="check" title={t('audit.attention.empty')} />
              )}
            </div>
          </div>

          <div className="card" data-od-id="audit-distribution">
            <div className="card-head">
              <h2>{t('audit.distribution.title')}</h2>
            </div>
            <div className="card-body stack">
              {distribution.map((row) => (
                <div key={row.key}>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span className="small">{t(`audit.filter.${row.key}`)}</span>
                    <span className="small num muted">{formatNumber(row.value)}</span>
                  </div>
                  <div className={row.tone ? `bar ${row.tone}` : 'bar'} style={{ marginTop: 5 }}>
                    <span style={{ width: `${Math.round((row.value / peak) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function FragmentRow({
  row,
  open,
  onToggle,
}: {
  row: AuditLogItem;
  open: boolean;
  onToggle: () => void;
}) {
  const { formatDateTime, formatRelative } = useFormat();
  const t = useT();
  const actorName = row.actorEmail ?? t('audit.system');

  return (
    <>
      <tr>
        <td className="num small">
          <button
            className="btn btn-sm btn-ghost"
            type="button"
            aria-expanded={open}
            onClick={onToggle}
            title={t('audit.expand')}
          >
            {formatDateTime(row.createdAt)}
          </button>
          <div className="sub">{formatRelative(row.createdAt)}</div>
        </td>
        <td>
          <div className="cell-main">
            <span className={`avatar sm ${row.actorId ? avatarTone(row.actorId) : ''}`}>
              {row.actorEmail ? initialsOf('', row.actorEmail) : 'SYS'}
            </span>
            <span>{actorName}</span>
          </div>
        </td>
        <td>{t(`action.${row.action}`)}</td>
        <td className="mono small">{row.targetLabel ?? row.targetType ?? '—'}</td>
        <td className="mono small">
          {hienIp(row.ip, {
            'may-nay': t('profile.sessions.ipLoopback'),
            'noi-bo': '',
            'cong-cong': '',
          })}
        </td>
        <td className="right">
          <span className={row.result === 'success' ? 'chip ok' : 'chip bad'}>
            <span className="dot" />
            {t(`result.${row.result}`)}
          </span>
        </td>
      </tr>
      {open ? <AuditRowDetail row={row} colSpan={COLUMNS} /> : null}
    </>
  );
}
